import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import Axios from 'axios';
import { Collapse, Icon } from '@material-ui/core';
import classNames from 'classnames';
import { SearchResultItem } from '../../../redux/reducers/SearchResultReducer';
import Form from '../../molecules/Form';
import Button from '../../atoms/Button';
import UpdateDate from '../../atoms/UpdateDate';
import {
  GASpotEventMappingState,
  SCREEN,
  SCROLL_TARGET_TYPE,
  addDate,
  dateToFormatString,
  formatDate,
  isPastOrEqualDay,
  stringDateToFormat,
  stringDateToFormatString,
} from '../../../Common';
import { isTargetDateBeforeComparisonDateByNDays } from '../../../utils/StringDateUtil';
import ProductCardItemComponent from '../../molecules/SearchResultProductCardItem';
import Tag from '../../atoms/Tag';
import { getPcmCdNmByPcmCdDivisionFromMasterInfo, MasterInfoState } from '../../../redux/reducers/MasterInfoReducer';
import NegotiationMemoPreviewModal from '../../organisms/NegotiationMemoPreviewModal';
import Notice from '../../atoms/Notice';
import { NegotiationResultProductInfo } from '../../../external/NegotiationListApi';
import { splitStringByLength } from '../../../utils/StringUtil';

export const DEFAULT_COLLAPSE_LENGTH = 2;
export const MAX_COLLAPSE_LENGTH = 20;

export interface SearchResultItemProps extends SearchResultItem {
  index: number;
  masterInfo: MasterInfoState;
  actionSpotEvent: {
    [key: string]: GASpotEventMappingState;
  };
  today: string;
}

interface SearchResultItemState {
  /**
   * 開閉状態. trueのときに閉じている
   * 他の行をクリックして連動して閉じるようにするには、isCollapsed, onHandleCollapse はpropsで受け渡し、上位階層で開閉状態を管理する
   */
  isCollapsed: boolean;
  isMemoModal: boolean;
}

/**
 * 画面に表示する有効期限を取得します
 * @param createDate 顧客作成日付(yyyy-MM-dd)
 * @param expirationDate 有効期限(yyyy-MM-dd)
 * @returns 画面表示有効期限(yyyy/MM/dd) or undefined
 * 無効な日付と判定された場合はundefinedを返却します
 */
const getEffectiveDate = (createDateStr?: string, expirationDateStr?: string) => {
  const addDayForPhysicalDelete = 1459; // 物理削除対象日(1460日) - 1日

  // お客さま情報作成日付、有効期限のどちらにも値がない場合(blank or undefined)、判定不可であるため、有効期限はundefinedとして、画面に表示させない
  if (!(createDateStr && expirationDateStr)) {
    return undefined;
  }
  // 各引数をDate型へ変換します
  const createDate = stringDateToFormat(createDateStr, 'yyyy-MM-dd');
  const expirationDate = stringDateToFormat(expirationDateStr, 'yyyy-MM-dd');

  // お客さま情報作成日付、有効期限のどちらも妥当な日付でない場合、判定不可であるため、有効期限はundefinedとして、画面に表示させない
  if (!(createDate && expirationDate)) {
    return undefined;
  }
  // 物理削除日を算出するために、お客さま情報作成日付に日数を加算します
  const effectiveDateOfPhysicalDelete = addDate(createDate, addDayForPhysicalDelete);

  // 物理削除日の方が有効期限よりも古い(現在から近い)日付である場合
  if (isPastOrEqualDay(effectiveDateOfPhysicalDelete, expirationDate)) {
    // 物理削除日を返却します
    return dateToFormatString(effectiveDateOfPhysicalDelete, 'yyyy/MM/dd');
  }
  // 有効期限の方が物理削除日よりも古い(現在から近い)日付である場合
  // 有効期限を返却します
  return dateToFormatString(expirationDate, 'yyyy/MM/dd');
};

export const getNegotiationResultProductByCollapsed = (
  isCollapsed: boolean,
  negotiationResultProductInfo: NegotiationResultProductInfo[] | undefined,
): NegotiationResultProductInfo[] => {
  if (!Array.isArray(negotiationResultProductInfo)) return [];
  if (isCollapsed) return negotiationResultProductInfo.slice(undefined, DEFAULT_COLLAPSE_LENGTH);
  return negotiationResultProductInfo.slice(undefined, MAX_COLLAPSE_LENGTH);
};

export const getRemainingProductLength = (
  sliceNegotiationResultProductInfo: NegotiationResultProductInfo[],
  negotiationResultProductInfo: NegotiationResultProductInfo[] | undefined,
): number => {
  if (!Array.isArray(negotiationResultProductInfo)) return 0;
  return negotiationResultProductInfo.length - sliceNegotiationResultProductInfo.length;
};

interface ProductCardItemListProps {
  sliceNegotiationResultProductInfo: NegotiationResultProductInfo[];
  masterInfo: MasterInfoState;
  today: string;
}

const ProductCardItemList = ({ sliceNegotiationResultProductInfo, masterInfo, today }: ProductCardItemListProps) => {
  return (
    <>
      {sliceNegotiationResultProductInfo.map((productInfo: NegotiationResultProductInfo) => (
        <ProductCardItemComponent
          commodityCategory={productInfo.commodityCategory}
          commodityContract={productInfo.commodityContract}
          negotiationStatus={productInfo.negotiationStatus}
          contractAccuracy={productInfo.contractAccuracy}
          commodityBrand={productInfo.commodityBrand}
          negotiationResultActionInfo={productInfo.negotiationResultActionInfo}
          masterInfo={masterInfo}
          today={today}
        />
      ))}
    </>
  );
};

interface ProductCardItemByCollapsedProps {
  sliceNegotiationResultProductInfo: NegotiationResultProductInfo[];
  masterInfo: MasterInfoState;
  isCollapsed: boolean;
}

interface ProductCardItemByCollapsedProps {
  sliceNegotiationResultProductInfo: NegotiationResultProductInfo[];
  masterInfo: MasterInfoState;
  isCollapsed: boolean;
  today: string;
}

const ProductCardItemByCollapsed = ({
  sliceNegotiationResultProductInfo,
  masterInfo,
  isCollapsed,
  today,
}: ProductCardItemByCollapsedProps) => {
  const defaultProductInfo = sliceNegotiationResultProductInfo.slice(undefined, DEFAULT_COLLAPSE_LENGTH);
  const otherProductInfo = sliceNegotiationResultProductInfo.slice(DEFAULT_COLLAPSE_LENGTH, MAX_COLLAPSE_LENGTH);

  return (
    <>
      <ProductCardItemList sliceNegotiationResultProductInfo={defaultProductInfo} masterInfo={masterInfo} today={today} />
      <Collapse in={!isCollapsed} unmountOnExit>
        <ProductCardItemList sliceNegotiationResultProductInfo={otherProductInfo} masterInfo={masterInfo} today={today} />
      </Collapse>
    </>
  );
};

export default class extends Component<SearchResultItemProps, SearchResultItemState> {
  private scrollRef: React.RefObject<HTMLLIElement>;
  constructor(props: SearchResultItemProps) {
    super(props);

    this.state = {
      isCollapsed: true,
      isMemoModal: false,
    };
    this.scrollRef = React.createRef<HTMLLIElement>();
  }

  setIsMemoModal() {
    this.setState({ isMemoModal: !this.state.isMemoModal });
  }

  addGASpotEventIntoActionButtons() {
    Object.values(this.props.actionSpotEvent).forEach(({ spotEvent, className }) => {
      document.querySelector(`.${className}-${this.props.index}`)?.setAttribute('onclick', spotEvent || '');
    });
  }

  componentDidMount() {
    this.addGASpotEventIntoActionButtons();
  }

  render() {
    const { isCollapsed, isMemoModal } = this.state;
    const {
      index,
      updateDate,
      createDate,
      negotiationId,
      customerId,
      ordcstmName,
      closingSalesmanName,
      otherSalesmanName,
      customerFullName,
      customerNumber,
      ownStoreFlg,
      projectCd,
      keep,
      cncgPrintNo,
      tempCd,
      dummyFlg,
      negotiationResultProductInfo,
      externalSystemLink1,
      masterInfo,
      resurrectionFlg,
      resurrectionDate,
      negotiationNote,
      customerCreateDate,
      expirationDate,
      actionSpotEvent,
      hasNegotiationAttachedFile,
      today,
    } = this.props;
    const detailList = [];
    const projectName = getPcmCdNmByPcmCdDivisionFromMasterInfo(masterInfo, '0154', projectCd);
    detailList.push(`案件名：${projectName ? projectName : '-'}`);
    detailList.push(`取り置き：${keep ? keep : '-'}`);
    detailList.push(`料金シミュレーター提案：${cncgPrintNo ? 'あり' : 'なし'}`);

    const tempCdBuff = tempCd ? tempCd : '-';
    const strbuff = tempCdBuff.split(/,/);
    const strList: string[] = [];
    strbuff.forEach((str, index) => {
      if (str) {
        if (index === 0) {
          strList.push(str === '0' ? '-' : str);
        } else {
          strList.push(str);
        }
      } else {
        strList.push('-');
      }
    });
    detailList.push(`臨時コード：${strList.join(',')}`);

    const sliceNegotiationResultProductInfo = getNegotiationResultProductByCollapsed(isCollapsed, negotiationResultProductInfo);
    const remainingProductLength = getRemainingProductLength(sliceNegotiationResultProductInfo, negotiationResultProductInfo);

    // 商品が3個以上の場合のみクリック可能
    const isClickable = negotiationResultProductInfo && negotiationResultProductInfo.length > DEFAULT_COLLAPSE_LENGTH;

    // クルーモジュール判定
    const isCrewModule = Axios.defaults.headers.common['module'] === 'crew';
    // 編集可能代理店判定(ログイン代理店、およびそのデュアル店舗である場合は、編集可能、それ以外は読み取り専用)
    const isLoginAgencyCd = ownStoreFlg === '1';
    // マスクが必要な商談が判定：クルー以外モジュール又は、復活予定あり(1)の商談
    const isMask = !isCrewModule || Number(resurrectionFlg) === 1 || !isLoginAgencyCd;
    // クルー、商談メモ1文字以上、復活予定内(0)の場合、商談メモボタン表示
    const isNegotiationNote = isCrewModule && negotiationNote && negotiationNote.length > 0 && Number(resurrectionFlg) === 0;
    // yyyy/MM/dd形式の復活日付
    const formattedResurrectionDate = resurrectionDate && stringDateToFormatString(resurrectionDate, 'yyyy-MM-dd', 'yyyy/MM/dd');

    // 有効期限取得関数
    // 復活予定である場合、有効期限はundefinedとして、画面に表示させない
    const effectiveDate =
      Number(resurrectionFlg) === 1 && formattedResurrectionDate ? undefined : getEffectiveDate(customerCreateDate, expirationDate);

    /**
     * 店舗名表示条件
     *  1 クルーモジュール以外の場合、表示(true)
     *  2 クルーモジュールであっても、読み取り専用代理店の場合、表示(true)
     *  上記以外の場合は表示しない(false)
     */
    const showStoreName = !isCrewModule || !isLoginAgencyCd;

    /**
     * 商談カードの左側(お客さま氏名,お客さま番号,担当者,店舗名)の表示テキストの改行考慮結果を取得します
     * @param input
     * @returns
     */
    const getLeftAreaDisplayText = (input: string) => {
      // 8文字未満の場合は判定処理自体が不要となるため、文字列の加工をスキップする
      if (input.length < 8) {
        return input;
      }
      // 16文字で改行するために文字列分割関数を実行
      const arrayString = splitStringByLength(input, 16);
      // 改行コードで配列をjoinします
      return arrayString.join('\n');
    };

    return (
      <>
        <li
          className={`search-result-card ${isCrewModule && !isLoginAgencyCd && 'search-result-readonly'} ${
            !isClickable && 'is-not-clickable'
          }`}
          onClick={
            isClickable
              ? () => {
                  this.setState({ isCollapsed: !isCollapsed });
                }
              : undefined
          }
          ref={this.scrollRef}
          tabIndex={index}
          data-testid='detail-button'
          data-testid-e2e='searchResultItem-searchResultCard'>
          {/* 復活予定案件である場合、復活日付を表示する} */}
          {Number(resurrectionFlg) === 1 && formattedResurrectionDate && (
            <div className='u-flex u-flex-column u-h-full u-ph16 u-pt16'>
              <Notice
                icon='info'
                titleLabel={`復活予定案件（${formattedResurrectionDate}から、閲覧・編集ができるようになります）`}
                isInline
                type='info'
                id='notice'
                data-testid='resurrection-date-message'
                data-testid-e2e='resurrection-date-message'
              />
            </div>
          )}

          <div className='search-result-card__content'>
            <div className='search-result-card__content-arrow'>
              <Icon
                className={classNames('search-result-card__content-arrow-icon', isCollapsed && 'is-rotate')}
                style={{
                  visibility: isClickable ? 'visible' : 'hidden',
                }}
                data-testid='searchResultItem-isCollapsedIcon'
                data-testid-e2e='searchResultItem-isCollapsedIcon'>
                keyboard_arrow_up
              </Icon>
            </div>
            <div className='search-result-card__content-col search-result-col-left u-min-ws'>
              {/* モジュールより表示内容変更} */}
              {!isMask && (
                <Form.Fileld label='お客さま氏名' className='u-mb16 uc-button--max-wml'>
                  <div data-testid-e2e='searchResultItem-customerFullName'>
                    {getLeftAreaDisplayText(customerFullName ? customerFullName : '-')}
                  </div>
                  <div data-testid-e2e='searchResultItem-customerNumber' className='uc-form-field__label u-text-disabled'>
                    {getLeftAreaDisplayText(`(${customerNumber ? customerNumber : ''})`)}
                  </div>
                </Form.Fileld>
              )}
              {/* マスクが必要な場合 */}
              {isMask && (
                <Form.Fileld label='お客さま番号' className='u-mb16 uc-button--max-wml'>
                  <div data-testid-e2e='searchResultItem-customerNumber'>
                    {getLeftAreaDisplayText(customerNumber ? `${customerNumber}` : '-')}
                  </div>
                </Form.Fileld>
              )}
              {/* 担当者} */}
              <Form.Fileld label='担当者' className='u-mb16'>
                <div data-testid='closingSalesmanName' data-testid-e2e='searchResultItem-closingSalesmanName'>
                  {getLeftAreaDisplayText(
                    closingSalesmanName ? closingSalesmanName : otherSalesmanName ? otherSalesmanName : 'その他担当者',
                  )}
                </div>
              </Form.Fileld>
              {/* 外部システム連携情報1(来店予約 00:来店予約なし、01:来店予約あり)、01の場合表示} */}
              {externalSystemLink1 === '01' && (
                <Form.Fileld label='' className='u-mb16'>
                  <Tag data-testid='visitShopTip' className='u-ml8 u-text-normal' label={'来店予約あり'} />
                </Form.Fileld>
              )}
              {/* クルーモジュール以外の場合、表示} */}
              {/* クルーモジュールであっても、読み取り専用代理店の場合、表示} */}
              {showStoreName && (
                <Form.Fileld label='店舗名' className='u-mb16'>
                  <div data-testid='ordcstmName' data-testid-e2e='searchResultItem-ordcstmName'>
                    {getLeftAreaDisplayText(ordcstmName ? ordcstmName : '-')}
                  </div>
                </Form.Fileld>
              )}
            </div>
            <div className='search-result-card__content-col u-flex-1'>
              {/* クルーモジュールかつご案内書類が存在する場合表示 */}
              {isCrewModule && hasNegotiationAttachedFile && (
                <div className='u-float-right' style={{ width: '124px', marginTop: '-4px' }}>
                  <Link
                    to={
                      Number(resurrectionFlg) === 1
                        ? '#'
                        : `${SCREEN.SUMMARY_PREVIEW.URL}/${customerId}/${SCROLL_TARGET_TYPE.NEGOTIATION_PHOTO}/${negotiationId}`
                    }>
                    <Button
                      type='transparent'
                      label='案内書類を表示'
                      disabled={Number(resurrectionFlg) === 1}
                      leftIcon='collections'
                      height='2s'
                      data-testid='to-negotiation-attached-file'
                      data-testid-e2e='to-negotiation-attached-file'
                    />
                  </Link>
                </div>
              )}
              <Form.Fileld label='ご案内商品'>
                {/* 商品がない場合 */}
                {(negotiationResultProductInfo && negotiationResultProductInfo.length === 0) ||
                  (negotiationResultProductInfo === undefined && (
                    <p className='u-text-disabled u-text-ml u-mv4'>ご案内中の商品はまだありません</p>
                  ))}
                {/* 商品がある場合 */}
                {sliceNegotiationResultProductInfo && (
                  <ProductCardItemByCollapsed
                    sliceNegotiationResultProductInfo={sliceNegotiationResultProductInfo}
                    isCollapsed={isCollapsed}
                    masterInfo={masterInfo}
                    today={today}
                  />
                )}
              </Form.Fileld>
              {remainingProductLength > 0 && (
                <div data-testid-e2e='searchResultItem-otherProductCount'>他{remainingProductLength}件の商品</div>
              )}
            </div>
            <div className='search-result-card__content-col' style={{ width: '116px' }}>
              {/* マスクが必要な場合 */}
              {isCrewModule && isLoginAgencyCd && (
                <>
                  <Link to={isMask || dummyFlg ? '#' : `${SCREEN.NEGOTIATION_DETAIL.URL}/${negotiationId}`}>
                    <Button
                      width='full'
                      className={`u-mb8 ${actionSpotEvent.toNegotiation.className}-${index}`}
                      // onClick={() => this.props.history.push(`${SCREEN.NEGOTIATION_DETAIL.URL}/todo`)}
                      label='商談の詳細'
                      disabled={isMask || dummyFlg}
                      data-testid='toNegotiation'
                    />
                  </Link>
                  <Link to={isMask ? '#' : `${SCREEN.CUSTOMER_DETAIL.URL}/${customerId}`}>
                    <Button
                      width='full'
                      type='outlined'
                      className={`u-mb8 ${actionSpotEvent.toCustomer.className}-${index}`}
                      // onClick={() => this.props.history.push(`${SCREEN.CUSTOMER_DETAIL.URL}/todo`)}
                      label='お客さま情報'
                      disabled={isMask}
                      data-testid='toCustomer'
                    />
                  </Link>
                </>
              )}
              <Link
                to={
                  Number(resurrectionFlg) === 1
                    ? '#'
                    : `${SCREEN.SUMMARY_PREVIEW.URL}/${customerId}/${SCROLL_TARGET_TYPE.NEGOTIATION}/${negotiationId}`
                }>
                <Button
                  width='full'
                  className={`u-mb8 ${actionSpotEvent.toSummaryPreview.className}-${index}`}
                  label='サマリー'
                  disabled={Number(resurrectionFlg) === 1}
                  data-testid='to-summary-preview'
                  data-testid-e2e='to-summary-preview'
                />
              </Link>
              {isCrewModule && isLoginAgencyCd && (
                <>
                  {isNegotiationNote && (
                    <Button
                      width='full'
                      type='outlined'
                      className={`u-mb8 ${actionSpotEvent.memoNegotiation.className}-${index}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        this.setIsMemoModal();
                      }}
                      label='商談メモ'
                      data-testid='search-result-item-negotiation-memo-button'
                    />
                  )}
                </>
              )}
              <p className='u-text-dark u-text-right'>
                <UpdateDate
                  size='small'
                  updateDate={formatDate(createDate, 'YYYY-MM-DD', 'YYYY/MM/DD')}
                  data-testid='created-date'
                  labelType='create'
                />
              </p>
              <p className='u-text-dark u-text-right'>
                <UpdateDate
                  size='small'
                  updateDate={formatDate(updateDate, 'YYYY-MM-DD', 'YYYY/MM/DD')}
                  data-testid='update-date'
                  labelType='update'
                  isDanger={isTargetDateBeforeComparisonDateByNDays(updateDate, today, 60)}
                />
              </p>
              {effectiveDate && (
                <p className='u-text-dark u-text-right'>
                  <UpdateDate size='small' updateDate={effectiveDate} data-testid='effective-date' labelType='deadline' />
                </p>
              )}
            </div>
          </div>
          <div className='search-result-card__details'>
            <div className={`search-result-table__detail-panel ${isCrewModule && !isLoginAgencyCd && 'search-result-readonly'}`}>
              <div className='uc-panel uc-panel--gray u-pv8' data-testid='detail-info' data-testid-e2e='searchResultItemState-detailInfo'>
                {detailList.join(' / ')}
              </div>
            </div>
          </div>
        </li>
        <NegotiationMemoPreviewModal
          isOpen={isMemoModal}
          negotiationMemo={negotiationNote}
          onClose={() => {
            this.setIsMemoModal();
          }}></NegotiationMemoPreviewModal>
      </>
    );
  }
}
